import { useEffect, useRef } from "react";

function getStorageValue<T>(key: string, initialValue: T, session: boolean): T {
  const storage = getStorage(session);
  if (!storage) {
    return initialValue;
  }

  try {
    const storageKey = formatStorageKey(key);
    const item = storage.getItem(storageKey);

    return item ? JSON.parse(item) : initialValue;
  } catch (error) {
    console.warn(`Error reading localStorage key '${key}':`, error);

    return initialValue;
  }
}

export function getSessionStorageValue<T>(key: string, initialValue: T): T {
  return getStorageValue(key, initialValue, true);
}

export function getLocalStorageValue<T>(key: string, initialValue: T): T {
  return getStorageValue(key, initialValue, false);
}

function updateStorage<T>(key: string, value: T, session: boolean): void {
  const storage = getStorage(session);
  if (!storage) {
    return;
  }

  try {
    const storageKey = formatStorageKey(key);
    const valueString = JSON.stringify(value);

    if (valueString) {
      storage.setItem(storageKey, valueString);
    } else {
      storage.removeItem(storageKey);
    }
  } catch (error) {
    console.warn(`Error setting storage key '${key}':`, error);
  }
}

export function updateLocalStorage<T>(key: string, value: T): void {
  return updateStorage(key, value, false);
}

export function updateSessionStorage<T>(key: string, value: T): void {
  return updateStorage(key, value, true);
}

function useUpdateStorage<T>(key: string, value: T, session: boolean): void {
  const didMountRef = useRef(false);

  useEffect(() => {
    // Don't update local storage on first mount, local storage value will not have changed
    if (didMountRef.current) {
      updateStorage(key, value, session);
    } else {
      didMountRef.current = true;
    }
  }, [key, value, session]);
}

export function useUpdateSessionStorage<T>(key: string, value: T): void {
  return useUpdateStorage(key, value, true);
}

export function clearLocalStorage(key: string): void {
  const storage = getStorage(false);
  if (!storage) {
    return;
  }

  try {
    const storageKey = formatStorageKey(key);
    storage.removeItem(storageKey);
  } catch (error) {
    console.warn(`Error clearing localStorage key '${key}':`, error);
  }
}

export function clearSessionStorage(key: string): void {
  const storage = getStorage(true);
  if (!storage) {
    return;
  }

  try {
    const storageKey = formatStorageKey(key);
    storage.removeItem(storageKey);
  } catch (error) {
    console.warn(`Error clearing sessionStorage key '${key}':`, error);
  }
}

export function useUpdateLocalStorage<T>(key: string, value: T): void {
  return useUpdateStorage(key, value, false);
}

function formatStorageKey(key: string): string {
  return `AoP.` + key;
}

function getStorage(session: boolean): Storage | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return session ? window.sessionStorage : window.localStorage;
  } catch {
    return undefined;
  }
}
